import { FC, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import { OnboardingModulesTypes } from '@frontend/api-onboarding-modules';
import { GradientPage } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useScheduleTrainingTaskConfig } from '@frontend/schedule-training-task';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Heading, Text } from '@frontend/design-system';
import { AppointmentCardList } from '../../components/appointment-card-list';
import { ModuleTasksCardList, ModuleTasksCardListLoader } from '../../components/module-tasks-card-list';
import {
  TasksCompletedNavigation,
  TasksScheduledNavigation,
  TrainingLinkNavigation,
} from '../../components/onboarding-navigation';
import { ProgressBar } from '../../components/progress-bar';
import { TaskStatusCard } from '../../components/task-status-card';
import { DASHBOARD_PATH } from '../../constants';
import { useOnboardingDashboardConfig } from '../../hooks/use-onboarding-dashboard-config';
import { useOnboardingTasksSkip } from '../../hooks/use-onboarding-tasks-skip';
import { OnboardingModuleTasksContext } from '../../provider/onboarding-module-tasks.provider';
import { OnboardingDashboardTrackingIds } from '../../tracking-ids';
import { ModuleTaskStats } from '../../types';
import { getModuleTaskStats, getTaskById } from '../../utils';

export const OnboardingDashboardPage: FC = () => {
  const { t } = useTranslation('onboardingDashboard');
  const navigate = useNavigate();

  const onboardingDashboardConfig = useOnboardingDashboardConfig();

  const {
    showOnboardingDashboard,
    onboardingModuleTasks = [],
    isLoading,
    isReady,
    enableSkipTasks,
    selectedLocationId,
  } = onboardingDashboardConfig;

  const { skipOnboardingTasks, isSkipInProgress } = useOnboardingTasksSkip({ locationId: selectedLocationId });
  const { hideTask: hideTrainingTask, isMutating: isHidingTrainingTask } = useScheduleTrainingTaskConfig({
    locationId: selectedLocationId,
    checkEligibility: false,
  });

  // Navigate to the dashboard if onboarding dashboard is not eligible
  useEffect(() => {
    if (isLoading) {
      return;
    } else if (isReady && !showOnboardingDashboard) {
      navigate({ replace: true, to: DASHBOARD_PATH });
    }
  }, [isReady, isLoading, showOnboardingDashboard, navigate]);

  const { completed, progress, scheduled, total } = useMemo<ModuleTaskStats>(() => {
    return getModuleTaskStats(onboardingModuleTasks);
  }, [onboardingModuleTasks]);

  const hasPendingScheduleTrainingTask = useMemo(() => {
    const task = getTaskById(OnboardingModulesTypes.TaskIds.Schedule1on1Training, onboardingModuleTasks);
    return task && task.status === OnboardingModulesTypes.TaskStatus.INCOMPLETE;
  }, [onboardingModuleTasks]);

  const handleSkipTasks = () => {
    skipOnboardingTasks();
    hasPendingScheduleTrainingTask && hideTrainingTask();
  };

  const showAllTasksCompleted = total > 0 && completed === total;
  const showAllTasksScheduled = total > 0 && scheduled > 0 && scheduled + completed === total;

  return (
    <OnboardingModuleTasksContext.Provider value={onboardingDashboardConfig}>
      <GradientPage pageStyles={styles.gradientPage}>
        <ContentLoader show={isSkipInProgress || isHidingTrainingTask} />
        <div css={styles.overflowLayout}>
          <div css={styles.container}>
            <TaskStatusCard>
              <TaskStatusCard.Header showLogo>
                <div css={styles.heading}>
                  <Heading level={3} color='white'>
                    {t('Welcome to Weave!')}
                  </Heading>
                  {enableSkipTasks && (
                    <Button
                      variant='tertiary'
                      aria-label='Skip Remaining Tasks'
                      onClick={handleSkipTasks}
                      disabled={isSkipInProgress}
                      trackingId={OnboardingDashboardTrackingIds.skipRemaining}
                      css={styles.skipButton}
                    >
                      <Text as='span' weight='bold' size='medium' color='white'>
                        {t('Skip Remaining')}
                      </Text>
                    </Button>
                  )}
                </div>
              </TaskStatusCard.Header>
              <TaskStatusCard.Body>
                {isLoading ? (
                  <>
                    <TaskStatusCard.Body.Loader />
                    <TaskStatusCard.Body.Loader />
                  </>
                ) : (
                  <>
                    <TaskStatusCard.Body.MilestoneText completed={completed} total={total} />
                    <ProgressBar progress={progress} containerStyles={{ flex: 1, minWidth: '40%' }} />
                  </>
                )}
              </TaskStatusCard.Body>
            </TaskStatusCard>
            {showAllTasksCompleted && <TasksCompletedNavigation />}
            {showAllTasksScheduled && <TasksScheduledNavigation />}
            {isLoading && <ModuleTasksCardListLoader />}
            {!isLoading && !!onboardingModuleTasks?.length && (
              <>
                <AppointmentCardList moduleTasks={onboardingModuleTasks} />
                <ModuleTasksCardList moduleTasks={onboardingModuleTasks} />
              </>
            )}
            <TrainingLinkNavigation />
          </div>
        </div>
      </GradientPage>
    </OnboardingModuleTasksContext.Provider>
  );
};

const styles = {
  gradientPage: css`
    overflow: auto;
  `,
  heading: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
  `,
  overflowLayout: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding: ${theme.spacing(2)};
  `,
  container: css`
    display: flex;
    flex-direction: column;
    margin: 40px auto;
    gap: ${theme.spacing(2)};
    width: min(100%, 580px);

    @media (max-width: ${breakpoints.mobile.min}px) {
      margin: 0;
    }
  `,
  skipButton: css`
    :hover {
      background-color: ${theme.colors.indigo40};
    }
  `,
};
